import sanitizeHtml from 'sanitize-html';
import { normalizeNonBreakingSpaces } from 'src/lib/rich-text-editor/normalizeNonBreakingSpaces';

const QUILL_ALIGN_CLASS_TO_TEXT_ALIGN: Record<string, string> = {
  'ql-align-left': 'left',
  'ql-align-center': 'center',
  'ql-align-right': 'right',
  'ql-align-justify': 'justify',
};

// Quill serialises block alignment as a `ql-align-*` class, which only maps
// to a visible `text-align` via Quill's own stylesheet (`.ql-editor .ql-align-*`).
// Renderers outside the editor (Resolution Hub, Vault Reporter Portal) don't load
// that stylesheet, so the class survives sanitization but never renders. Convert
// it to an inline style here so alignment displays wherever this HTML ends up.
const alignClassToInlineStyle = (
  tagName: string,
  attribs: Record<string, string>,
) => {
  const classes = (attribs.class ?? '').split(/\s+/).filter(Boolean);
  const alignClass = classes.find((className) =>
    Object.prototype.hasOwnProperty.call(
      QUILL_ALIGN_CLASS_TO_TEXT_ALIGN,
      className,
    ),
  );
  const { class: _class, ...rest } = attribs;
  if (!alignClass) {
    return { tagName, attribs: rest };
  }
  return {
    tagName,
    attribs: {
      ...rest,
      style: `text-align:${QUILL_ALIGN_CLASS_TO_TEXT_ALIGN[alignClass]}`,
    },
  };
};

interface SanitizeQuillHtmlOptions {
  normalizeSpaces?: boolean;
}

export const sanitizeQuillHtml = (
  html: string,
  { normalizeSpaces = false }: SanitizeQuillHtmlOptions = {},
): string =>
  sanitizeHtml(normalizeSpaces ? normalizeNonBreakingSpaces(html) : html, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'ol',
      'ul',
      'li',
      'a',
      'h1',
      'h2',
      'h3',
      'blockquote',
      'code',
      'pre',
    ],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      p: ['style'],
      h1: ['style'],
      h2: ['style'],
      h3: ['style'],
      blockquote: ['style'],
      li: ['style'],
    },
    allowedStyles: {
      p: { 'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/] },
      h1: { 'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/] },
      h2: { 'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/] },
      h3: { 'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/] },
      blockquote: {
        'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/],
      },
      li: { 'text-align': [/^left$/, /^center$/, /^right$/, /^justify$/] },
    },
    transformTags: {
      p: alignClassToInlineStyle,
      h1: alignClassToInlineStyle,
      h2: alignClassToInlineStyle,
      h3: alignClassToInlineStyle,
      blockquote: alignClassToInlineStyle,
      li: alignClassToInlineStyle,
    },
  });
